import React from "react";
import { useLocation, matchPath, useHistory } from "react-router-dom";
import { Breadcrumb, Skeleton } from "antd";
import { useEventName, useStudentName, useCompanyName } from "./useBreadCrumbName";

type RouteConfig = {
  path: string;
  breadcrumb: string | ((params: Record<string, string>) => React.ReactNode);
};

const EventBreadcrumb: React.FC<{ id: string }> = ({ id }) => {
  const name = useEventName(id);
  return name ? <span>{name}</span> : <Skeleton.Input active size="small" />;
};

const StudentBreadcrumb: React.FC<{ id: string }> = ({ id }) => {
  const name = useStudentName(id);
  return name ? <span>{name}</span> : <Skeleton.Input active size="small" />;
};

const CompanyBreadcrumb: React.FC<{ id: string }> = ({ id }) => {
  const name = useCompanyName(id);
  return name ? <span>{name}</span> : <Skeleton.Input active size="small" />;
};

const adminRoutes: RouteConfig[] = [
  { path: "/admin/", breadcrumb: "Dashboard" },
  { path: "/admin/events/", breadcrumb: "Arrangementer" },
  { path: "/admin/events/new/", breadcrumb: "Velg bedrift" },
  { path: "/admin/events/:id/", breadcrumb: ({ id }) => (id !== "new" ? <EventBreadcrumb id={id} /> : null) },
  { path: "/admin/events/:id/edit/", breadcrumb: "Rediger arrangement" },
  { path: "/admin/events/:id/seats/", breadcrumb: "Rediger plassfordeling" },
  { path: "/admin/events/new/:id", breadcrumb: "Opprett nytt arrangement" },
  { path: "/admin/companies/", breadcrumb: "Bedrifter" },
  { path: "/admin/companies/:id", breadcrumb: ({ id }) => <CompanyBreadcrumb id={id} /> },
  { path: "/admin/companies/:id/edit-job/:jobId", breadcrumb: "Rediger stillingsannonse" },
  { path: "/admin/companies/:id/create-new-job/", breadcrumb: "Opprett ny stillingsannonse" },
  { path: "/admin/students/", breadcrumb: "Studenter" },
  { path: "/admin/students/:id", breadcrumb: ({ id }) => <StudentBreadcrumb id={id} /> },
  { path: "/admin/resources/articles/", breadcrumb: "Artikler" },
  { path: "/admin/resources/casebank/", breadcrumb: "Casebank" },
  { path: "/admin/resources/casebank/new/", breadcrumb: "Velg bedrift" },
  { path: "/admin/resources/casebank/new/:id/", breadcrumb: "Opprett ny case" },
  { path: "/admin/resources/casebank/edit/:id/", breadcrumb: "Rediger case" },
  { path: "/admin/resources/publications/", breadcrumb: "Publikasjoner" },
  { path: "/admin/members/", breadcrumb: "Medlemmer" },
  { path: "/admin/tools/", breadcrumb: "Verktøy" },
  { path: "/admin/tools/analyse/", breadcrumb: "Analyse" },
  { path: "/admin/tools/send-email/", breadcrumb: "Send e-post" },
  { path: "/admin/tools/booking-seasons/", breadcrumb: "Bookingsesonger" },
  { path: "/admin/tools/booking-seasons/new/", breadcrumb: "Opprett ny bookingsesong" },
  { path: "/admin/tools/booking-seasons/edit/:id", breadcrumb: "Rediger bookingsesong" },
  { path: "/admin/tools/take-attendance-for-event/", breadcrumb: "Innslippsappen" },
];

const findMatchingRoutes = (routes: RouteConfig[], pathname: string) => {
  return routes
    .map(route => {
      const match = matchPath(pathname, {
        path: route.path,
        exact: false,
      });
      if (match && route.path === "/admin/events/:id/" && (match.params as { id: string }).id === "new") {
        // Prevent matching /admin/events/new/ to /admin/events/:id/ with new as id
        return null;
      }
      return match ? { route, params: match.params } : null;
    })
    .filter((result): result is { route: RouteConfig; params: Record<string, string> } => result !== null);
};

const AdminBreadcrumb: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const matches = findMatchingRoutes(adminRoutes, location.pathname);

  const breadcrumbs = matches.map(({ route, params }) => {
    const breadcrumbName = typeof route.breadcrumb === "function" ? route.breadcrumb(params) : route.breadcrumb;
    const path = Object.keys(params).reduce(
      (resolvedPath, paramKey) => resolvedPath.replace(`:${paramKey}`, params[paramKey]),
      route.path
    );
    return { path, breadcrumbName };
  });

  return (
    <Breadcrumb style={{ marginBottom: "16px" }} separator=">">
      {breadcrumbs.map(breadcrumb => (
        <Breadcrumb.Item key={breadcrumb.path}>
          <span
            onClick={() => history.push(breadcrumb.path)}
            onKeyPress={e => {
              if (e.key === "Enter" || e.key === " ") {
                history.push(breadcrumb.path);
              }
            }}
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
          >
            {breadcrumb.breadcrumbName}
          </span>
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );
};

export default AdminBreadcrumb;
